import React from "react";
import { Badge, HashCell, AddressCell, TxHashCell, RowShareBtn } from "@/components/primitives";
import { formatLovelace, truncMid, unixToDate } from "@/lib/format";

// ---------- tab configs (one set per explorer mode) ----------
export const ACCOUNT_TABS = [
  { key: "history", label: "History", path: "/history" },
  { key: "withdrawals", label: "Withdrawals", path: "/withdrawals" },
  { key: "rewards", label: "Rewards", path: "/rewards" },
  { key: "addresses", label: "Addresses", shape: "wallet_addresses", colKey: "addresses" },
];

export const ADDRESS_TABS = [
  { key: "total", label: "Total", path: "/total", shape: "total" },
  { key: "txs", label: "Tx hashes", path: "/txs", shape: "hashes" },
  { key: "transactions", label: "Tx list", path: "/transactions", shape: "address_transactions" },
  { key: "wallet", label: "All Addresses", shape: "wallet_addresses", colKey: "addresses" },
];

export const ASSET_TABS = [
  { key: "history", label: "History", path: "/history", colKey: "asset_history" },
  { key: "transactions", label: "Transactions", path: "/transactions", colKey: "asset_transactions" },
  { key: "addresses", label: "Holders", path: "/addresses", colKey: "asset_holders" },
  {
    key: "policy",
    label: "Policy assets",
    // custom path: /assets/policy/{policy_id} (first 56 chars of identifier)
    pathFn: (id) => `/assets/policy/${(id || "").slice(0, 56)}`,
    colKey: "asset_policy",
  },
];

export const BLOCK_TABS = [
  { key: "transactions", label: "Transactions", path: "/txs", shape: "hashes", colKey: "block_txs" },
  { key: "addresses", label: "Affected addresses", path: "/addresses", colKey: "block_addresses" },
  { key: "next", label: "Next blocks", path: "/next", colKey: "block_list" },
  { key: "previous", label: "Previous blocks", path: "/previous", colKey: "block_list" },
];

export const TX_TABS = [
  { key: "flow", label: "TX Flow", path: "/utxos", shape: "tx_flow" },
  { key: "utxos", label: "UTXOs", path: "/utxos", shape: "tx_utxos" },
  { key: "metadata", label: "Metadata", path: "/metadata", colKey: "tx_metadata" },
];

export const POOL_TABS = [
  { key: "history", label: "Epoch history", path: "/history", colKey: "pool_history" },
];

export const EPOCH_TABS = [
  { key: "blocks", label: "Blocks in epoch", path: "/blocks", colKey: "block_list" },
];

export const METADATA_TABS = [
  { key: "entries", label: "Transactions", path: "", colKey: "metadata_label_entries" },
];

export const tabsFor = (modeKey) => {
  if (modeKey === "addresses") return ADDRESS_TABS;
  if (modeKey === "assets") return ASSET_TABS;
  if (modeKey === "blocks") return BLOCK_TABS;
  if (modeKey === "transactions") return TX_TABS;
  if (modeKey === "pools") return POOL_TABS;
  if (modeKey === "epochs") return EPOCH_TABS;
  if (modeKey === "metadata") return METADATA_TABS;
  return ACCOUNT_TABS;
};

export const columnsFor = (tab, amountMode) => {
  switch (tab) {
    case "rewards":
      return [
        { key: "epoch", label: "Epoch", align: "right" },
        {
          key: "pool_id",
          label: "Pool",
          render: (r) => <HashCell value={r.pool_id} len={12} />,
        },
        { key: "type", label: "Type", render: (r) => <Badge tone="info">{r.type}</Badge> },
        {
          key: "amount",
          label: "Amount",
          align: "right",
          render: (r) => formatLovelace(r.amount, amountMode),
        },
      ];
    case "history":
      return [
        { key: "active_epoch", label: "Active Epoch", align: "right" },
        {
          key: "pool_id",
          label: "Pool",
          render: (r) => <HashCell value={r.pool_id} len={12} />,
        },
        {
          key: "amount",
          label: "Stake",
          align: "right",
          render: (r) => formatLovelace(r.amount, amountMode),
        },
      ];
    case "withdrawals":
      return [
        { key: "block_height", label: "Block", align: "right" },
        {
          key: "block_time",
          label: "Time",
          render: (r) => <span className="text-muted-foreground">{unixToDate(r.block_time)}</span>,
        },
        {
          key: "tx_hash",
          label: "Tx",
          render: (r) => <TxHashCell value={r.tx_hash} />,
        },
        {
          key: "amount",
          label: "Amount",
          align: "right",
          render: (r) => formatLovelace(r.amount, amountMode),
        },
      ];
    case "addresses":
      return [
        {
          key: "address",
          label: "Address",
          render: (r) => (
            <span className="inline-flex items-center gap-2">
              <AddressCell value={r.address} len={30} />
              <RowShareBtn modeKey="addresses" id={r.address} />
            </span>
          ),
        },
        {
          key: "utxo_count",
          label: "Live UTXOs",
          align: "right",
          render: (r) => (r.utxo_count !== undefined ? r.utxo_count : "—"),
        },
        {
          key: "tx_count",
          label: "Tx Count",
          align: "right",
          render: (r) => (r.tx_count !== undefined ? r.tx_count : "—"),
        },
        {
          key: "balance",
          label: "Balance",
          align: "right",
          render: (r) =>
            r.balance !== undefined ? formatLovelace(r.balance, amountMode) : "—",
        },
      ];
    case "assets":
      return [
        {
          key: "unit",
          label: "Unit (policy + name hex)",
          render: (r) => <HashCell value={r.unit} len={30} />,
        },
        { key: "quantity", label: "Quantity", align: "right" },
      ];
    case "utxos":
      return [
        {
          key: "address",
          label: "Address",
          render: (r) => <AddressCell value={r.address} len={24} />,
        },
        {
          key: "tx_hash",
          label: "Tx",
          render: (r) => (
            <span>
              <HashCell value={r.tx_hash} />
              <span className="text-muted-foreground"> #{r.output_index}</span>
            </span>
          ),
        },
        {
          key: "amount",
          label: "Amount",
          align: "right",
          render: (r) => {
            const lovelace = r.amount.find((a) => a.unit === "lovelace")?.quantity || "0";
            const assets = r.amount.filter((a) => a.unit !== "lovelace");
            return (
              <span className="inline-flex flex-col items-end">
                <span>{formatLovelace(lovelace, amountMode)}</span>
                {assets.length ? (
                  <span className="text-muted-foreground text-[10px] mt-1">
                    +{assets.length} asset{assets.length > 1 ? "s" : ""}
                  </span>
                ) : null}
              </span>
            );
          },
        },
      ];
    case "transactions":
      return [
        {
          key: "tx_hash",
          label: "Trx Hash",
          render: (r) => <TxHashCell value={r.tx_hash} len={12} />,
        },
        { key: "block_height", label: "Block", align: "right" },
        {
          key: "block_time",
          label: "Time",
          render: (r) => <span className="text-muted-foreground">{unixToDate(r.block_time)}</span>,
        },
        {
          key: "fee",
          label: "Fees",
          align: "right",
          render: (r) =>
            r.fee !== undefined ? (
              <span className="text-muted-foreground">{formatLovelace(r.fee, amountMode)}</span>
            ) : (
              "—"
            ),
        },
        {
          key: "ada_spent",
          label: "ADA Spent",
          align: "right",
          render: (r) =>
            r.ada_spent && r.ada_spent !== "0" ? (
              <span className="text-rose-400">- {formatLovelace(r.ada_spent, amountMode)}</span>
            ) : (
              <span className="text-muted-foreground/50">—</span>
            ),
        },
        {
          key: "ada_received",
          label: "ADA Received",
          align: "right",
          render: (r) =>
            r.ada_received && r.ada_received !== "0" ? (
              <span className="text-emerald-400">+ {formatLovelace(r.ada_received, amountMode)}</span>
            ) : (
              <span className="text-muted-foreground/50">—</span>
            ),
        },
      ];
    case "utxos_lovelace":
      return [
        {
          key: "tx_hash",
          label: "Tx",
          render: (r) => (
            <span>
              <HashCell value={r.tx_hash} />
              <span className="text-muted-foreground"> #{r.output_index}</span>
            </span>
          ),
        },
        {
          key: "amount",
          label: "Amount",
          align: "right",
          render: (r) => {
            const lovelace = r.amount.find((a) => a.unit === "lovelace")?.quantity || "0";
            return formatLovelace(lovelace, amountMode);
          },
        },
      ];
    case "txs":
      return [
        {
          key: "tx_hash",
          label: "Tx hash",
          render: (r) => <TxHashCell value={r.value} len={20} />,
        },
      ];
    case "asset_history":
      return [
        { key: "block_height", label: "Block", align: "right" },
        {
          key: "block_time",
          label: "Time",
          render: (r) => <span className="text-muted-foreground">{unixToDate(r.block_time)}</span>,
        },
        {
          key: "action",
          label: "Action",
          render: (r) => (
            <Badge tone={r.action === "minted" ? "success" : "warn"}>{r.action}</Badge>
          ),
        },
        {
          key: "amount",
          label: "Amount",
          align: "right",
          render: (r) => (
            <span className={r.action === "minted" ? "text-emerald-400" : "text-rose-400"}>
              {r.action === "minted" ? "+ " : "- "}
              {Number(r.amount).toLocaleString("en-US")}
            </span>
          ),
        },
        { key: "tx_hash", label: "Tx", render: (r) => <TxHashCell value={r.tx_hash} /> },
      ];
    case "asset_transactions":
      return [
        {
          key: "tx_hash",
          label: "Trx Hash",
          render: (r) => <TxHashCell value={r.tx_hash} len={12} />,
        },
        { key: "block_height", label: "Block", align: "right" },
        {
          key: "block_time",
          label: "Time",
          render: (r) => <span className="text-muted-foreground">{unixToDate(r.block_time)}</span>,
        },
        { key: "tx_index", label: "Index", align: "right" },
      ];
    case "asset_holders":
      return [
        {
          key: "address",
          label: "Address",
          render: (r) => (
            <span className="inline-flex items-center gap-2">
              <AddressCell value={r.address} len={30} />
              <RowShareBtn modeKey="addresses" id={r.address} />
            </span>
          ),
        },
        {
          key: "quantity",
          label: "Quantity",
          align: "right",
          render: (r) => Number(r.quantity).toLocaleString("en-US"),
        },
      ];
    case "asset_policy":
      return [
        {
          key: "asset",
          label: "Asset",
          render: (r) => <HashCell value={r.asset} len={28} />,
        },
        {
          key: "quantity",
          label: "Live supply",
          align: "right",
          render: (r) => Number(r.quantity).toLocaleString("en-US"),
        },
      ];
    case "block_txs":
      return [
        {
          key: "tx_hash",
          label: "Tx hash",
          render: (r) => <TxHashCell value={r.value} len={26} />,
        },
      ];
    case "block_addresses":
      return [
        {
          key: "address",
          label: "Address",
          render: (r) => (
            <span className="inline-flex items-center gap-2">
              <AddressCell value={r.address} len={32} />
              <RowShareBtn modeKey="addresses" id={r.address} />
            </span>
          ),
        },
        {
          key: "transactions",
          label: "Tx hashes",
          render: (r) => (
            <div className="flex flex-col gap-1">
              {(r.transactions || []).map((t) => (
                <TxHashCell key={t.tx_hash} value={t.tx_hash} len={16} />
              ))}
            </div>
          ),
        },
      ];
    case "block_list":
      return [
        { key: "height", label: "Height", align: "right" },
        {
          key: "time",
          label: "Time",
          render: (r) => <span className="text-muted-foreground">{unixToDate(r.time)}</span>,
        },
        { key: "tx_count", label: "Txs", align: "right" },
        {
          key: "fees",
          label: "Fees",
          align: "right",
          render: (r) => formatLovelace(r.fees, amountMode),
        },
        {
          key: "hash",
          label: "Hash",
          render: (r) => (
            <span className="inline-flex items-center gap-2">
              <HashCell value={r.hash} len={14} />
              <RowShareBtn modeKey="blocks" id={r.height != null ? String(r.height) : r.hash} />
            </span>
          ),
        },
      ];
    case "tx_metadata":
      return [
        { key: "label", label: "Label", align: "right" },
        {
          key: "json_metadata",
          label: "JSON",
          render: (r) => (
            <pre className="text-[11px] mono whitespace-pre-wrap break-words max-w-[600px]">
              {JSON.stringify(r.json_metadata, null, 2)}
            </pre>
          ),
        },
        {
          key: "cbor_metadata",
          label: "CBOR (hex)",
          render: (r) => (
            <span className="mono text-[11px] text-muted-foreground break-all">
              {r.cbor_metadata ? truncMid(r.cbor_metadata, 14, 8) : "—"}
            </span>
          ),
        },
      ];
    case "metadata_label_entries":
      return [
        {
          key: "tx_hash",
          label: "Tx Hash",
          render: (r) => <TxHashCell value={r.tx_hash} len={14} />,
        },
        { key: "block_height", label: "Block", align: "right" },
        {
          key: "block_time",
          label: "Time",
          render: (r) => <span className="text-muted-foreground">{unixToDate(r.block_time)}</span>,
        },
        {
          key: "json_metadata",
          label: "JSON",
          render: (r) => (
            <pre className="text-[11px] mono whitespace-pre-wrap break-words max-w-[520px]">
              {JSON.stringify(r.json_metadata, null, 2)}
            </pre>
          ),
        },
      ];
    case "pool_history":
      return [
        { key: "epoch", label: "Epoch", align: "right" },
        {
          key: "active_stake",
          label: "Active stake",
          align: "right",
          render: (r) => formatLovelace(r.active_stake, amountMode),
        },
        { key: "delegators_count", label: "Delegators", align: "right" },
      ];
    default:
      return [];
  }
};

// Returns true if `id` is shaped like a valid identifier for `modeKey`.
// Used to suppress stale fetches across mode transitions where React batches
// `identifier` and `mode` updates separately.
export const identifierMatchesMode = (modeKey, id) => {
  if (!id) return false;
  if (modeKey === "accounts") return /^stake(_test)?1[a-z0-9]+$/.test(id);
  if (modeKey === "addresses") return /^addr(_test)?1[a-z0-9]+$/.test(id);
  if (modeKey === "assets") return /^[0-9a-f]{56,120}$/.test(id) && id.length % 2 === 0;
  if (modeKey === "blocks") {
    return id === "latest" || /^\d+$/.test(id) || /^[0-9a-f]{64}$/.test(id);
  }
  if (modeKey === "transactions") return /^[0-9a-f]{64}$/.test(id);
  if (modeKey === "pools") return /^pool1[a-z0-9]+$/.test(id);
  if (modeKey === "epochs") return id === "latest" || /^\d+$/.test(id);
  if (modeKey === "metadata") return /^\d+$/.test(id);
  return false;
};
